import type { ActionContext } from 'vuex'
import { defineModule } from 'direct-vuex'
import type { RootState } from '@/store/types'
import type { RoomSchedulesState } from '../types'
import { moduleActionContext } from '@/store'
import studio, { AdminDocNames, CollectionNames } from '@/modules/database/utils'
import {
  AdminFanRoomScheduleConfigs,
  type FanRoomSchedule, type FanRoomScheduleConfigManual, type FanRoomScheduleRow, type FanRoomScheduleTimeRange
} from '../classes'
import type { KeyDict, ResultVoid } from 'kiswe-ui'
import { DatabaseAction, MergeOption, type WithFieldActions } from '@/modules/database/utils/database'
import { generateRandomString } from '@/modules/common/utils'

type RoomSchedulesContext = ActionContext<RoomSchedulesState, RootState>

interface UpdateScheduleRangeParams {
  disciplineId: string
  range: FanRoomScheduleTimeRange
  rangeId: string
  scheduleId: string
}

interface UpdateScheduleRowParams {
  disciplineId: string
  row: FanRoomScheduleRow
  scheduleId: string
}

interface RemoveScheduleRowParams {
  disciplineId: string
  scheduleId: string
}

interface UpdateScheduleDisciplinesParams {
  disciplinesUpdates: KeyDict<WithFieldActions<Pick<FanRoomScheduleRow, 'ranges'>>>
  scheduleId: string
}

interface UpdateScheduleParams {
  schedule: FanRoomSchedule
  scheduleId: string
}

const state: RoomSchedulesState = {
  schedules: {},
  scheduleConfigs: new AdminFanRoomScheduleConfigs({ manual: {}, timed: {} })
}

const roomSchedulesModule = defineModule({
  namespaced: true,
  state,
  actions: {
    async subscribeSchedule (context: RoomSchedulesContext, teamId: string): Promise<void> {
      const { commit } = roomSchedulesModuleActionContext(context)
      const result = await studio.subscriptions.subscribe({
        key: `room_schedules_${teamId}`,
        query: studio.db.collection(CollectionNames.FANROOM_SCHEDULES)
          .where('teamId', '==', teamId)
          .where('deleted', '==', false),
        callback: commit.update
      })
      result.logIfError('Could not subscribe to fanroom_schedules')
    },
    unsubscribeSchedules (context: RoomSchedulesContext, teamId: string): void {
      const { commit } = roomSchedulesModuleActionContext(context)
      studio.subscriptions.unsubscribe(`room_schedules_${teamId}`, () => {
        commit.update({})
      })
    },
    async subscribeScheduleConfigs (context: RoomSchedulesContext): Promise<void> {
      const { commit } = roomSchedulesModuleActionContext(context)
      const result = await studio.subscriptions.subscribe({
        key: 'room_schedule_configs',
        query: studio.db.collection(CollectionNames.ADMIN).doc(AdminDocNames.FANROOM_SCHEDULES),
        callback: commit.setScheduleConfigs
      })
      result.logIfError('Could not subscribe to admin fanroom_schedules')
    },
    unsubscribeScheduleConfigs (context: RoomSchedulesContext): void {
      const { commit } = roomSchedulesModuleActionContext(context)
      studio.subscriptions.unsubscribe('room_schedule_configs', () => {
        commit.setScheduleConfigs({ manual: {}, timed: {} })
      })
    },
    updateScheduleRange (
      _context: RoomSchedulesContext, params: UpdateScheduleRangeParams
    ): Promise<ResultVoid<'database'>> {
      return studio.db.collection(CollectionNames.FANROOM_SCHEDULES).doc(params.scheduleId).set({
        [`disciplines.${params.disciplineId}.ranges.${params.rangeId}`]: params.range
      }, MergeOption.UPDATE)
    },
    updateScheduleRow (
      _context: RoomSchedulesContext, params: UpdateScheduleRowParams
    ): Promise<ResultVoid<'database'>> {
      return studio.db.collection(CollectionNames.FANROOM_SCHEDULES).doc(params.scheduleId).set({
        [`disciplines.${params.disciplineId}`]: params.row
      }, MergeOption.UPDATE)
    },
    removeScheduleRow (
      _context: RoomSchedulesContext, params: RemoveScheduleRowParams
    ): Promise<ResultVoid<'database'>> {
      return studio.db.collection(CollectionNames.FANROOM_SCHEDULES).doc(params.scheduleId).set({
        disciplines: {
          [params.disciplineId]: DatabaseAction.deleteField()
        }
      }, MergeOption.MERGE)
    },
    setSchedule (_context: RoomSchedulesContext, params: UpdateScheduleParams) {
      return studio.db.collection(CollectionNames.FANROOM_SCHEDULES).doc(params.scheduleId)
        .set(params.schedule, MergeOption.OVERWRITE)
    },
    updateScheduleDisciplinesRanges (
      _context: RoomSchedulesContext, params: UpdateScheduleDisciplinesParams
    ): Promise<ResultVoid<'database'>> {
      return studio.db.collection(CollectionNames.FANROOM_SCHEDULES).doc(params.scheduleId).set({
        disciplines: params.disciplinesUpdates
      }, MergeOption.MERGE)
    },
    addManualConfig (
      _context: RoomSchedulesContext, config: FanRoomScheduleConfigManual
    ): Promise<ResultVoid<'database'>> {
      const configId = generateRandomString(20)
      return studio.db.collection(CollectionNames.ADMIN).doc(AdminDocNames.FANROOM_SCHEDULES).set({
        manual: {
          [configId]: config
        }
      }, MergeOption.MERGE)
    }
  },
  mutations: {
    setScheduleConfigs (state: RoomSchedulesState, configs: AdminFanRoomScheduleConfigs): void {
      state.scheduleConfigs = configs
    },
    update (state: RoomSchedulesState, schedules: KeyDict<FanRoomSchedule>): void {
      state.schedules = schedules
    }
  }
})

export default roomSchedulesModule
export const roomSchedulesModuleActionContext = (context: RoomSchedulesContext) => {
  return moduleActionContext(context, roomSchedulesModule)
}
